/**
 * Created by piotr.pozniak@thebeaverhead.com on 19/12/2022
 */

import React, { useContext, useMemo } from "react";
import WidgetSettingsContext from "../../../../contexts/WidgetSettingsContext";
import { useTranslation } from "react-i18next";
import IntegrationContext from "../../strategies/IntegrationContext";
import { IT } from "../../../../consts";
import classNames from "classnames";

const cleanLocationString = (str) => {
  if (!str) return null;

  return str
    .split(",")
    .map((s) => s.trim())
    .filter((segment) => {
      // drop "67 km Athens", "5 miles from X" etc
      if (/^\d+\s*(km|mi|miles?)\b/i.test(segment)) return false;
      // drop postal codes like "195 00", "90210"
      if (/^\d{3,6}(\s\d{2,4})?$/.test(segment)) return false;
      return true;
    })
    .join(", ")
    .replace(/,+$/, "")
    .trim();
};

/**
 * Creates a Google Maps URL for the event location
 * @param {string|null} displayLocation
 * @param {object} location
 * @param {string} integrationType
 * @returns {string}
 */
export const generateGoogleLinkFromLocation = (
  displayLocation,
  location,
  integrationType = ""
) => {
  let hasSearchableLocation = (location && location.name) || displayLocation;

  if (!hasSearchableLocation) {
    return null;
  }

  if (integrationType === IT.google && !location?.hasSearchableLocation) {
    return null;
  }

  // for google integration with a resolved place ID
  if (integrationType === IT.google && location?.name?.startsWith("places/")) {
    const placeId = location.name.replace("places/", "");
    return `https://www.google.com/maps/search/?api=1&query_place_id=${placeId}`;
  }

  const locationChunks = [
    displayLocation,
    location.line_1,
    location.line_1 !== location.street_address
      ? location.street_address
      : null,
    location.line_2,
    location.city,
  ]
    .map((chunk) => chunk?.replace(/,+$/, "").trim()) // clean each chunk
    .filter(Boolean); // remove empty/null

  if (!locationChunks.length) {
    return null;
  }

  const cleanedLocation = cleanLocationString(displayLocation);

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    cleanedLocation
  )}`;
};

/**
 * EventDetailsLocation component
 * @param {Object} location
 * @param {string} displayLocation
 * @returns {React.JSX.Element|null}
 * @constructor
 */
const EventDetailsLocation = ({ location, displayLocation }) => {
  const calendarSettingsContext = useContext(WidgetSettingsContext);
  const integrationContext = useContext(IntegrationContext);
  const { t } = useTranslation();

  const showLocation =
    Number.parseInt(calendarSettingsContext.eventDetailsShowLocation) === 1;

  const locationAsLinkValue =
    Number.parseInt(calendarSettingsContext.eventDetailsLinkLocation) === 1;

  const linkLocation = showLocation && locationAsLinkValue;

  const hasLocationAddress =
    location &&
    (displayLocation != null ||
      location.line_1 != null ||
      location.line_2 != null ||
      location.street_address);

  const locationUrl = generateGoogleLinkFromLocation(
    displayLocation,
    location,
    integrationContext.type
  );

  // extract url from the displayLocation string, if it does not contain a url it is null
  const displayLocationUrl = useMemo(() => {
    if (displayLocation) {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const url = displayLocation.match(urlRegex);
      return url ? url[0] : null;
    }
    return null;
  }, [displayLocation]);

  const showLocationAsLink = showLocation && linkLocation && locationUrl;
  // if displayLocation contains a url, use that instead of the locationUrl
  const locationUrlToUse = useMemo(() => {
    return displayLocationUrl ? displayLocationUrl : locationUrl;
  }, [displayLocationUrl, locationUrl]);

  const locationBlock = useMemo(
    () =>
      showLocation && hasLocationAddress ? (
        <React.Fragment>
          <div className="col-icon">
            <i className="material-icons">room</i>
          </div>

          <div>
            <h3 className="">{t("Event location")}</h3>
            <div
              className={classNames("dc-address-link", {
                "dc-address-link_url": locationUrl ? true : false,
              })}
            >
              {displayLocation ? (
                <p>{displayLocation}</p>
              ) : location.name ? (
                <h4>{location.name}</h4>
              ) : null}
              <p className="">
                {location.line_1 ? (
                  <span>
                    {location.line_1}
                    <br />
                  </span>
                ) : null}
                {location.street_address &&
                (!location.line_1 ||
                  location.line_1 != location.street_address) ? (
                  <span>
                    {location.street_address}
                    <br />
                  </span>
                ) : null}

                {location.line_2 ? (
                  <span>
                    {location.line_2}
                    <br />
                  </span>
                ) : null}
              </p>
              {showLocationAsLink && !displayLocationUrl ? (
                <a href={locationUrlToUse} target={"_blank"}>
                  <button className={"dc-address-link--link"}>
                    {t("Show on map")}{" "}
                    <i className={"material-icons"}>open_in_new</i>
                  </button>
                </a>
              ) : null}
            </div>
          </div>
        </React.Fragment>
      ) : null,
    []
  );

  return <div className="col-content col-content--left">{locationBlock}</div>;
  /*
  showLocationAsLink ? (
    <a
      className={"col-content col-content--left"}
      href={locationUrlToUse}
      target={"_blank"}
    >
      {locationBlock}
    </a>
  ) : hasLocationAddress ? (

  ) : null;*/
};

export default EventDetailsLocation;
